import React, { useMemo, useState } from "react";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import {
  calculateDataTableState, constructQueryParamsString,
  getSortDirection, handleMultiLineImportErrors, handlePageSizeChange,
  showErrorToast, showSuccessToast,
  SORT_DIRECTIONS,
  SUPPLIER_STATUSES
} from "../../../../utils";
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { FaBars, FaPen, FaPlay, FaTimes, FaFileExport } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { DataTable } from "../../../../components/DataTable";
import {
  supplierAdministrationKeys,
  useManageSupplierCompany,
  useGetPaginatedSuppliersWithTiers,
  useExportSupplierCompanies,
  useManageSupplierStatus
} from "../../../../hooks/query/supplier-administration";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { SupplierCompanyModal } from "./index";
import {
  useDownloadSupplierCategoriesTemplate,
  useImportSupplierCategories
} from "../../../../hooks/query";
import { ImportData } from "../../../../components/ImportData";

function SupplierCompanies() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierManagement"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const buttonsContainerFlow = useBreakpointValue({ base: "column", xl: "row" });

  const [suppliersList, setSuppliersList] = useState([]);
  const [supplierCompanyModalData, setSupplierCompanyModalData] = useState(undefined);
  const {
    isOpen: isSupplierCompanyModalOpen,
    onOpen: onSupplierCompanyModalOpen,
    onClose: onSupplierCompanyModalClose
  } = useDisclosure();

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [`supplier.name,${SORT_DIRECTIONS.ASC}`],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calculateDataTableState({
          prevState,
          newValObject,
          sortAccessors: [
            { originalAccessor: "supplier", alternativeAccessor: "supplier.name" },
            { originalAccessor: "category", alternativeAccessor: "supplier.category.name" },
            { originalAccessor: "parent", alternativeAccessor: "parent.name" },
            { originalAccessor: "status", alternativeAccessor: "supplier.status" }
          ]
        })
      };
    });
  };

  const getQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    return constructQueryParamsString(queryParams);
  };

  const {
    isLoading: isLoadingSuppliers,
    isFetching: isFetchingSuppliers
  } = useGetPaginatedSuppliersWithTiers(getQueryParamsString(),
    {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const suppliers = pageContent.map((el) => {
          return {
            id: el.id,
            supplier: el.name,
            category: el.category,
            tier: el.tier,
            parentId: el.parentId,
            parent: el.parentName ?? "",
            status: el.status
          };
        });
        setSuppliersList(suppliers);
        handlePageSizeChange(data, queryPageIndex, manageDataTableState);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  const getRequestConfig = (successMessage) => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: () => {
        if (isSupplierCompanyModalOpen) {
          closeSupplierCompanyModal();
        }
        showSuccessToast(toast, successMessage);
        queryClient.invalidateQueries(supplierAdministrationKeys.paginatedSuppliersWithTiers());
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner())
    };
  };

  const { mutate: manageSupplierCompany } = useManageSupplierCompany(
    getRequestConfig(t("supplierCompanies.messages.detailsUpdated")));

  const { mutate: manageSupplierStatus } = useManageSupplierStatus(
    getRequestConfig(t("supplierCompanies.messages.detailsUpdated")));

  const columns = useMemo(() => {
      function handleManageSupplierCompany(data) {
        const {
          id, supplier, category, tier, parentId
        } = data.row.original;
        setSupplierCompanyModalData({
          id, supplier, category, tier, parent: parentId
        });

        onSupplierCompanyModalOpen();
      }

    function handleActivateSupplier(data) {
      manageSupplierStatus({
        id: data?.row?.original.id,
        status: SUPPLIER_STATUSES.ACTIVE
      });
    }

    function handleDeactivateSupplier(data) {
      manageSupplierStatus({
        id: data?.row?.original.id,
        status: SUPPLIER_STATUSES.DEACTIVATED
      });
    }

      return [
        {
          header: t("supplierCompanies.gridHeaders.supplier"),
          accessorKey: "supplier",
          size: 200,
          sortDirection: getSortDirection(queryPageSortBy, "supplier.name")
        },
        {
          header: t("supplierCompanies.gridHeaders.category"),
          accessorKey: "category",
          size: 150,
          sortDirection: getSortDirection(queryPageSortBy, "supplier.category.name")
        },
        {
          header: t("supplierCompanies.gridHeaders.tier"),
          accessorKey: "tier",
          size: 100,
          sortDirection: getSortDirection(queryPageSortBy, "tier")
        },
        {
          header: t("supplierCompanies.gridHeaders.parent"),
          accessorKey: "parent",
          size: 200,
          sortDirection: getSortDirection(queryPageSortBy, "parent.name"),
          enableColumnFilter: false
        },
        {
          header: t("supplierCompanies.gridHeaders.status"),
          accessorKey: "status",
          size: 150,
          sortDirection: getSortDirection(queryPageSortBy, "supplier.status"),
          enableColumnFilter: false,
          cell: cell => (
            <Badge
              backgroundColor={cell.row.original.status === SUPPLIER_STATUSES.ACTIVE ? "green.100" : "red.100"}
              color={cell.row.original.status === SUPPLIER_STATUSES.ACTIVE ? "green.800" : "red.800"}
              height="max-content"
            >
              {cell.row.original.status}
            </Badge>
          )
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("supplierCompanies.gridHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 90,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuItem
                  icon={<FaPen color="#6EA161" />}
                  onClick={() => handleManageSupplierCompany(row)}
                >
                  {t("supplierCompanies.buttons.manageSupplier")}
                </MenuItem>
                {(row.row.original.status === SUPPLIER_STATUSES.DEACTIVATED ||
                  row.row.original.status === SUPPLIER_STATUSES.INACTIVE) && <MenuItem
                  icon={<FaPlay color="#6EA161" />}
                  onClick={() => handleActivateSupplier(row)}
                >
                  {t("supplierCompanies.buttons.activateSupplier")}
                </MenuItem>}
                {row.row.original.status === SUPPLIER_STATUSES.ACTIVE &&
                  <MenuItem
                    icon={<FaTimes color="#6EA161" />}
                    onClick={() => handleDeactivateSupplier(row)}
                  >
                    {t("supplierCompanies.buttons.deactivateSupplier")}
                  </MenuItem>}
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, queryPageSortBy, onSupplierCompanyModalOpen, manageSupplierStatus]
  );

  const handleAddNewSupplier = () => {
    onSupplierCompanyModalOpen();
  };

  const closeSupplierCompanyModal = () => {
    setSupplierCompanyModalData(undefined);
    onSupplierCompanyModalClose();
  };

  const { mutate: exportSupplierCompanies } = useExportSupplierCompanies({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleExport = () => {
    exportSupplierCompanies();
  };

  return (
    <Box marginBlockStart="5">
      <Flex
        marginBlockEnd="5"
        justifyContent={"space-between"}
        flexFlow={buttonsContainerFlow}
        gap={buttonsContainerFlow === "column" ? 2 : 0}
      >
        <Flex gap={2}>
          <Button
            colorScheme="customScheme"
            width="fit-content"
            onClick={handleAddNewSupplier}
          >
            + {t("supplierCompanies.buttons.addNewSupplier")}
          </Button>
          <Button
            width="fit-content"
            colorScheme="customScheme"
            onClick={handleExport}
          >
            <Icon
              as={FaFileExport}
              inlineSize="3"
              blockSize="3"
              marginEnd="2"
            />
            {globalTranslation("buttons.exportData")}
          </Button>
        </Flex>
        <ImportData
          importDataBtnId={"import-categories-data"}
          importDataHook={useImportSupplierCategories}
          importDataConfig={{
            onSuccess: () => {
              showSuccessToast(toast, globalTranslation("messages.importSuccess"));
              queryClient.invalidateQueries(supplierAdministrationKeys.paginatedSuppliersWithTiers());
            },
            onError: err => handleMultiLineImportErrors(err, toast),
            onSettled: () => dispatch(hideAppSpinner())
          }}
          downloadTemplateHook={useDownloadSupplierCategoriesTemplate}
        />
      </Flex>

      {isSupplierCompanyModalOpen && <SupplierCompanyModal
        modalData={supplierCompanyModalData}
        isOpen={isSupplierCompanyModalOpen}
        onClose={closeSupplierCompanyModal}
        onSubmit={(data) => manageSupplierCompany(data)}
      />}

      <DataTable
        data={suppliersList}
        columns={columns}
        isLoading={isLoadingSuppliers || isFetchingSuppliers}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
        queryPageSortBy={queryPageSortBy}
        setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
        filterObj={filterObj}
        setFilterObj={(newFilterObj) => manageDataTableState({
          filterObj: newFilterObj,
          queryPageIndex: 0
        })}
        enableFilters={true}
        enableSorting={true}
        containerHeight="var(--chakra-sizes-2xl)"
      />
    </Box>
  );
}

export default SupplierCompanies;